/* eslint-disable indent */
import { reduce, get, keyBy, round } from 'lodash'
import { formatTimeShort } from './format'
import { DateTime } from 'luxon'

/**
 * Sentinel selectedPaymentMethodId representing the Apple Pay / Google Pay
 * (Express Checkout Element) option rather than a saved card. Mirrors the RN
 * app's PLATFORM_PAY (mantis/app/utils/constants) so both clients agree.
 */
export const PLATFORM_PAY = 'PLATFORM_PAY'

/**
 * Thrown by the booking flow when Next / Book Now is pressed in a render where
 * the current step has not registered its handler yet, e.g. Step Five while the
 * client's profile reloads after a token refresh. Nothing was sent to Starbase;
 * the client only needs to try again once the step has loaded. Has its own
 * name so analytics can tell it apart from a genuine TypeError.
 */
export class StepNotReadyError extends Error {
  constructor() {
    super('Your checkout is still loading. Please try again in a moment.')
    this.name = 'StepNotReadyError'
  }
}

/**
 * Determines if a service should display a duration range (e.g. "45-60 mins")
 * based on its model flags. Informational, add-on, wedding, and event services
 * display exact duration only.
 */
export const shouldShowDurationRange = (service) => {
  if (!service) {
    return false
  }

  const typeName = (service.service?.serviceType?.name || service.serviceType?.name || '').toLowerCase()
  const serviceName = (service.name || '').toLowerCase()

  return (
    !service.isInformational &&
    !service.isAddOn &&
    typeName !== 'weddings' &&
    typeName !== 'events' &&
    !serviceName.includes('hourly')
  )
}

export const formatDurationDisplay = (duration, service) => {
  if (shouldShowDurationRange(service)) {
    return `${duration}-${duration + 15}`
  }
  return `${duration}`
}

/**
 * placesResponseToAddress: converts a response from google places into an address object
 *
 * @param {*} place
 * @returns
 */
export const placesResponseToAddress = (place) => {
  if (!(place && place.address_components)) {
    return
  }

  // confirm existence of street number, street name, and zip code
  const hasStreetNumber = place.address_components?.some(component => component.types.includes('street_number'))
  const hasPostalCode = place.address_components?.some(component => component.types.includes('postal_code'))
  const hasRoute = place.address_components?.some(component => component.types.includes('route'))
  const locationName = place?.name
  const isUniqueAddressName = !(place.formatted_address.toLowerCase().includes(locationName.toLowerCase()))

  // not full address
  if (!hasStreetNumber || !hasPostalCode || !hasRoute) {
    return
  }

  const addressComponents = reduce(
    place.address_components,
    (acc, value) => ({
      ...acc,
      [value.types[0]]: value.long_name,
    }),
    {}
  )
  // Starbase rejects an address with an empty city. Some places carry no locality: Google
  // returns unincorporated towns such as Cypress, TX as a neighborhood, UK-style places as a
  // postal_town and some townships as administrative_area_level_3. The city segment of the
  // formatted address ("street, city, ST zip, country") is the last resort.
  const city = (
    addressComponents.sublocality_level_2
    || addressComponents.sublocality_level_1
    || addressComponents.locality
    || addressComponents.political
    || addressComponents.neighborhood
    || addressComponents.postal_town
    || addressComponents.administrative_area_level_3
    || place.formatted_address?.split(',')[1]?.trim()
  )
  const address = {
    formatted: place.formatted_address,
    city,
    state: addressComponents.administrative_area_level_1,
    zip: addressComponents.postal_code,
    latitude: round(place.geometry.location.lat(), 7),
    longitude: round(place.geometry.location.lng(), 7),
    name: isUniqueAddressName ? locationName : ''
  }
  address.street = addressComponents.street_number ? `${addressComponents.street_number} ${addressComponents.route}` : `${addressComponents.route}`

  if (address?.name) {
    address.formatted = address.name
  }
  return address
}

/**
 * serviceAndAddOnServiceNames: converts a service and add-on ids into names
 *
 * @param {*} marketServices
 * @param {*} { serviceId, addOnServiceIds }
 * @returns
 */
export const serviceAndAddOnServiceNames = (marketServices, { serviceId, addOnServiceIds }, marketId = null) => {
  if (!marketServices?.length) {
    return {}
  }
  
  if (marketId) {
    marketServices = marketServices.filter(ms => ms?.marketId === marketId)
  }

  const marketServicesByServiceId = keyBy(marketServices, 'service.id')

  if (!marketServicesByServiceId[serviceId]) {
    return {} // return an empty object if we're not loaded yet
  }

  const {
    service: { name: serviceName },
    addOnMarketServices,
  } = marketServicesByServiceId[serviceId]

  const addOnMarketServicesByServiceId = keyBy(addOnMarketServices, 'service.id')

  return {
    serviceName,
    addOnServiceNames: addOnServiceIds.map(addOnServiceId =>
      get(addOnMarketServicesByServiceId, [addOnServiceId, 'service', 'addOnName']))
  }
}

/**
 * serviceAndAddOnServiceInfo: converts a service and add-on ids into names
 *
 * @param {*} marketServices
 * @param {*} { serviceId, addOnServiceIds }
 * @returns
 */
export const serviceAndAddOnServiceInfo = (marketServices, { baseServiceId, addOnServiceIds = [] }) => {
  const marketServicesByServiceId = keyBy(marketServices, 'service.id')

  const marketService = marketServicesByServiceId[baseServiceId]

  if (!marketService) {
    return {} // return an empty object if we're not loaded yet
  }

  const {
    price,
    addOnPrice,
    displayPrice,
    duration,
    addOnDuration,
    service: {
      name,
      addOnName,
    },
    addOnMarketServices,
  } = marketService

  const addOnServices = addOnServiceIds.map(addOnServiceId => serviceAndAddOnServiceInfo(addOnMarketServices, { baseServiceId: addOnServiceId }))

  // normalize returned add-on and base service attributes. displayPrice is the
  // market's client-facing price (base, or base + fees/tip depending on the
  // market's priceDisplay setting — computed server-side); it collapses to the
  // base price for 'Base' markets.
  return {
    name: addOnName || name,
    price: addOnPrice || price,
    displayPrice,
    duration: addOnDuration || duration,
    addOnServices,
  }
}

/**
 * confirmationServiceDetails: per-unique-service summary for the booking
 * confirmation (step six) — the service name, its add-on names, and its
 * "things to know" prep tips. Deduplicates by serviceId so a service booked for
 * multiple instances or back-to-back appears once, preserving booking order, and
 * unions add-on names across a repeated service. tips is an empty array when the
 * service carries no (or malformed) thingsToKnow, letting the confirmation both
 * list every booked service and render prep guidance only where it exists.
 *
 * Uses the completed appointment rather than the original cart, which can still
 * contain services or add-ons omitted by an alternate availability selection.
 * Accepts both service ids and populated baseService/addOnServices from Starbase.
 *
 * @param {array} marketServices market services with a nested service that
 *   includes name and thingsToKnow ([{ title, body }])
 * @param {array<object>} appointmentServices the completed appointment's services
 * @param {string} [marketId] when set, scopes market services to this market
 * @returns {array<{ serviceId: string, name: string, addOnNames: string[], tips: Array<{ title: string, body: string }> }>}
 */
export const confirmationServiceDetails = (
  marketServices,
  appointmentServices = [],
  marketId = null
) => {
  const scoped = marketId
    ? (marketServices || []).filter(ms => ms?.marketId === marketId)
    : (marketServices || [])
  const marketServicesByServiceId = keyBy(scoped, 'service.id')

  const order = []
  const detailsByServiceId = {}

  for (const appointmentService of appointmentServices || []) {
    const serviceId =
      appointmentService.baseServiceId ||
      appointmentService.baseService?.id ||
      appointmentService.serviceId ||
      appointmentService.service?.id
    const addOnServiceIds =
      appointmentService.addOnServiceIds ??
      (appointmentService.addOnServices || []).map(addOn => addOn.id)
    const info = serviceAndAddOnServiceInfo(scoped, { baseServiceId: serviceId, addOnServiceIds })
    if (!info.name) {
      continue // service not in this market's catalog (or not loaded yet)
    }

    if (!detailsByServiceId[serviceId]) {
      const service = marketServicesByServiceId[serviceId]?.service
      const tips = Array.isArray(service?.thingsToKnow) ? service.thingsToKnow : []
      detailsByServiceId[serviceId] = { serviceId, name: info.name, addOnNames: [], tips }
      order.push(serviceId)
    }

    for (const addOn of info.addOnServices || []) {
      if (addOn?.name && !detailsByServiceId[serviceId].addOnNames.includes(addOn.name)) {
        detailsByServiceId[serviceId].addOnNames.push(addOn.name)
      }
    }
  }

  return order.map(id => detailsByServiceId[id])
}

/**
 * if no availability results, check if requests are for multiple services
 * @param {array<serviceRequest>} serviceRequests { addOnServices: [], backToBack: boolean, instances: number, serviceId: string }
 * @returns {boolean}
 */
export const hasMultiServiceRequests = (serviceRequests) => {
  let id = serviceRequests[0].serviceId
  for (let i = 1; i < serviceRequests.length; i++) {
    if (serviceRequests[i].serviceId !== id) {
      return true
    }
  }
  return false
}

/**
 * formats the retry availability search button
 * @param {array<serviceRequest>} originalRequests all service requests
 * @param {array<serviceRequest>} validRequests filtere requests that are available
 * @param {array<marketService} marketServices all market services
 * @returns string— "Try again without x and x"
 */
export const formatBookingRetryButton = (originalRequests, validRequests, marketServices) => {

  let message = 'Try Again Without '

  const withoutString =
    originalRequests
      .filter(sr => !validRequests.find(vsr => vsr.serviceId === sr.serviceId))
      .map(s => {
        return marketServices.find(ms => ms.service.id === s.serviceId).service.name
      })

  switch (withoutString.length) {
    case 1:
      return message + withoutString[0]
    case 2:
      return message + `${withoutString[0]} and ${withoutString[1]}`
    default:
      return message + `${withoutString.slice(0, -1).join(', ')} and ${withoutString.slice(-1)}`
  }
}

/**
 * returns an array representing the arrival window for an appointment
 * appends 15 minutes to startDateTime
 * @param {string} startDateTime
*/
export const getArrivalWindow = (startDateTime, timezone) => {
  const arrival = formatTimeShort(startDateTime, timezone)
  const endDateTime = DateTime.fromSeconds(startDateTime).plus({ minutes: 15 }).toISO()
  const end = formatTimeShort(endDateTime, timezone)

  return [arrival, end]
}

/** Return the latest service completion time, including the arrival window. */
export const getAppointmentEndTime = (appointment, timezone) => {
  const endSeconds = Math.max(...appointment.appointmentServices.map(service =>
    service.startDateTime + (service.duration + 15) * 60
  ))
  return formatTimeShort(DateTime.fromSeconds(endSeconds).toISO(), timezone)
}

/**
 * totalServiceInstances: total number of service instances in the cart
 * (sum of each request's instances), used for the "My Services (N)" count.
 *
 * @param {array} serviceRequests
 * @returns {number}
 */
export const totalServiceInstances = (serviceRequests = []) =>
  (serviceRequests || []).reduce((total, sr) => total + (sr?.instances || 1), 0)

/**
 * servicesUnavailableInMarket: the subset of cart requests whose service does not exist in a
 * market. This is the single source of truth shared by the cross-market address warning (show it
 * only when something would actually be lost) and Two.js's cart pruning (remove exactly those
 * items) — so the two can never disagree. Services are matched on serviceId, the market-
 * independent catalog id; add-on-level availability is intentionally not considered.
 *
 * @param {array} serviceRequests cart entries, each with a serviceId
 * @param {array} marketServices market-service rows; scoped by marketId when marketId is given,
 *   otherwise assumed already scoped to a single market
 * @param {string} [marketId] the market to check availability in
 * @returns {array} the serviceRequests that are unavailable in that market
 */
export const servicesUnavailableInMarket = (serviceRequests = [], marketServices = [], marketId = null) => {
  const scoped = marketId
    ? (marketServices || []).filter(ms => ms?.marketId === marketId)
    : (marketServices || [])

  const marketServicesByServiceId = keyBy(scoped, 'service.id')

  return (serviceRequests || []).filter(sr => !marketServicesByServiceId[sr?.serviceId])
}

/**
 * cartPricing: client-side price estimate for the cart drawer. Add-on quantity is
 * tied to the service quantity, so every line total scales by instances — the cart
 * shows a true per-line total (unlike the single-item service-detail modal, which
 * shows the per-one price).
 *
 * Each line/add-on carries displayLineTotal — the market's client-facing display
 * price × instances (displayPrice is base for 'Base' markets and fee/tip-inclusive
 * for 'Base + Fees[& Tip]', falling back to the base price when a service has none).
 * This is exactly what the cart renders.
 *
 * serviceTotal (and therefore subtotal) stays base-derived so estimateTaxesAndFees
 * can add tax/fees without double-counting the fees already inside displayPrice for
 * 'Base + Fees' markets, and so the subtotal matches the server's aggregate-rounded
 * checkout price. This excludes travel/tax/tip (applied by the server at step 5).
 *
 * @param {array} marketServices
 * @param {array} serviceRequests
 * @param {string} marketId
 * @returns {{ lines: array, subtotal: number }}
 */
export const cartPricing = (marketServices, serviceRequests = [], marketId = null) => {
  const scoped = marketId
    ? (marketServices || []).filter(ms => ms?.marketId === marketId)
    : (marketServices || [])

  const lines = (serviceRequests || []).map(({ serviceId, instances = 1, addOnServiceIds = [] }) => {
    const info = serviceAndAddOnServiceInfo(scoped, { baseServiceId: serviceId, addOnServiceIds })
    const basePrice = info.price || 0
    const displayPrice = info.displayPrice ?? basePrice
    const addOnServices = info.addOnServices || []
    const addOns = addOnServices.map(addOn => {
      const addOnDisplay = addOn.displayPrice ?? (addOn.price || 0)
      return {
        name: addOn.name,
        displayLineTotal: addOnDisplay * instances,
      }
    })
    const addOnBaseUnitSum = addOnServices.reduce((sum, addOn) => sum + (addOn.price || 0), 0)

    return {
      serviceId,
      name: info.name,
      instances,
      displayLineTotal: displayPrice * instances,
      addOns,
      serviceTotal: instances * (basePrice + addOnBaseUnitSum),
    }
  })

  const subtotal = lines.reduce((sum, line) => sum + line.serviceTotal, 0)

  return { lines, subtotal }
}

/**
 * estimateTaxesAndFees: client-side estimate of tax + handling fee on a services
 * subtotal, mirroring the Starbase price calc where market.serviceTax and
 * market.handlingFee are rates applied to the service total (see
 * starbase models/appointment.js calculatePrice). Excludes gratuity, account
 * credit, and address-dependent additional fees. Returns zeros and hasRates
 * false when the market carries no rate data, so callers can fall back to the
 * plain subtotal.
 *
 * @param {number} subtotal - services subtotal (from cartPricing).
 * @param {object|null} market - booking-state market carrying serviceTax/handlingFee.
 * @returns {{ taxAmount: number, handlingFee: number, taxesAndFees: number, total: number, hasRates: boolean }}
 */
export const estimateTaxesAndFees = (subtotal, market) => {
  const base = Number.isFinite(subtotal) ? subtotal : 0
  const serviceTax = market?.serviceTax
  const handlingFeeRate = market?.handlingFee
  const hasRates = Number.isFinite(serviceTax) || Number.isFinite(handlingFeeRate)

  const taxAmount = round((serviceTax || 0) * base, 3)
  const handlingFee = round((handlingFeeRate || 0) * base, 2)
  const taxesAndFees = taxAmount + handlingFee

  return { taxAmount, handlingFee, taxesAndFees, total: base + taxesAndFees, hasRates }
}

/**
 * isFreshBookingStart: true when the booking flow has no progress worth
 * preserving and no ?mkt deep-link owns the entry, so the straight-to-service
 * experiment may push the user to step 2. A market, any cart items, a step past
 * 2, or a ?mkt query all mean "not fresh; leave the persisted flow alone".
 */
export const isFreshBookingStart = (state = {}, query = {}) =>
  !query?.mkt &&
  !state?.market &&
  !(state?.serviceRequests?.length) &&
  (state?.step == null || state.step <= 2)
